/**
 * Conversão de bounding boxes entre o espaço normalizado [0,1] (contrato
 * backend) e pixels do canvas (E8.F4.T04).
 *
 * Convenção do backend (`apps/api/types/case_source_documents.py::BoundingBox`):
 * origem top-left, eixo x cresce à direita, eixo y cresce para baixo,
 * `x, y ∈ [0,1]`, `w, h ∈ (0,1]`, `x + w <= 1`, `y + h <= 1`.
 *
 * O canvas Fabric.js usa pixels com a mesma origem top-left, então a
 * conversão é uma multiplicação direta por `canvasWidth` / `canvasHeight`.
 *
 * `canvasToBbox` faz clamp em [0,1] para que arrasto/redimensionamento
 * que extravase a página seja recortado ao invés de falhar na validação
 * Pydantic do backend. `w` e `h` mínimos são forçados a um épsilon
 * estritamente positivo para respeitar o invariante `gt=0` do schema.
 */

export interface NormalizedBoundingBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CanvasRectPixels {
  left: number;
  top: number;
  width: number;
  height: number;
}

const EPSILON = 1e-6;

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/**
 * Converte um bbox normalizado em coordenadas pixel do canvas.
 *
 * Não faz clamp na entrada — assume que o bbox veio do backend já válido.
 * Se `canvasWidth` ou `canvasHeight` forem não-positivos, retorna o
 * retângulo degenerado na origem.
 */
export function bboxToCanvas(
  bbox: NormalizedBoundingBox,
  canvasWidth: number,
  canvasHeight: number,
): CanvasRectPixels {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  return {
    left: bbox.x * canvasWidth,
    top: bbox.y * canvasHeight,
    width: bbox.w * canvasWidth,
    height: bbox.h * canvasHeight,
  };
}

/**
 * Converte um retângulo em pixels do canvas (saída do Fabric) para um
 * bbox normalizado válido segundo o schema do backend.
 *
 * Clampa as quatro coordenadas em [0,1]. Se o retângulo ficou totalmente
 * fora da página depois do clamp (área zero), força `w` e `h` para
 * `EPSILON` mantendo `x + w <= 1` e `y + h <= 1`. O backend rejeitaria
 * uma caixa com área exatamente 0; chamadores podem optar por descartar
 * caixas com área < limiar antes de submeter.
 */
export function canvasToBbox(
  rect: CanvasRectPixels,
  canvasWidth: number,
  canvasHeight: number,
): NormalizedBoundingBox {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return { x: 0, y: 0, w: EPSILON, h: EPSILON };
  }
  const xRaw = rect.left / canvasWidth;
  const yRaw = rect.top / canvasHeight;
  const wRaw = rect.width / canvasWidth;
  const hRaw = rect.height / canvasHeight;

  const x = clampUnit(xRaw);
  const y = clampUnit(yRaw);
  const maxW = Math.max(EPSILON, 1 - x);
  const maxH = Math.max(EPSILON, 1 - y);
  const w = Math.min(maxW, Math.max(EPSILON, clampUnit(wRaw)));
  const h = Math.min(maxH, Math.max(EPSILON, clampUnit(hRaw)));

  return { x, y, w, h };
}
